/* eslint-disable vue/max-len */
import Vue from 'vue';
import {
  ListFactory, SuperDate, isValidArray, isValidObject, Money, isArray,
} from '@razlet/utils';
import EntityFactory from '../base/factories/entity';
import Contract from '../contract';
import Person from '../person';
import Booking from '../booking';
import PricingDetalization from '../pricing/detalization';
import Fop from '../fop';
import Attachment from '../attachment';
import ShipmentCargo from './cargo';
import ShipmentMovement from './movement';
import ShipmentPi from './pi';
import ShipmentWarehouse from './warehouse';
import ShipmentRefundBid from './refund-bid';
import ShipmentHistory from './history';
import ShimpentTerminalOrder from './terminal-order';
import { buildShipmentTracking, calculateTrackingProgress } from './tracking';
import List from '~/api/entities/base/list';

export default class Shipment {
  data = null;

  cargo = null;
  consignee = null;
  shipper = null;
  expeditor = null;
  cabinetShipper = null;

  bookings = [];
  movements = [];
  attachments = [];
  route = [];
  fopList = [];
  fopRefundList = [];

  eawb = null;
  awbPreviewHtml = '';
  letterPreviewHtml = '';

  createdAt = null;
  acceptedAt = null;
  issuedAt = null;
  issueDay = null;
  refundedAt = null;

  timelimitAt = null;

  pi = null;

  customsWarehouse = null;

  detalization = null;
  totalDetalization = null;
  soldDetalization = null;
  refundDetalization = null;

  terminalOrder = null;

  price = null;

  refundBid = null;

  history = new List({}, ShipmentHistory);

  constructor (data) {
    this.refill(data);
  }

  get warnings () {
    return isValidArray(this.data?.warnings) ? this.data.warnings : [];
  }

  get hasWarnings () {
    if (!this.warnings || !this.warnings.length) return false;
    return this.warnings
      .filter(warning => warning.area !== 'agreement' && warning.level !== 'info').length > 0;
  }

  refill (data) {
    if (!data) return;
    data = {
      ...data,
      mAwb: data.mAwb || data.mawb || data.awb || '',
      route: {
        departure: '',
        arrival: '',
        ...(data.route || {}),
      },
    };
    this.data = data;
    this.cargo = new ShipmentCargo(data);

    if (data.consignee) this.consignee = new Person(data.consignee);
    if (data.shipper) this.shipper = new Person(data.shipper);
    this.expeditor = new Contract(data.expeditor || {});
    if (data.cabinetShipper) this.cabinetShipper = new Contract(data.cabinetShipper);

    if (isArray(data.bookings)) {
      this.bookings = data.bookings.map(booking => EntityFactory.createRow(
        Booking,
        {
          ...booking,
          shipmentCompany: this.company,
        },
        { disabled: !booking.active }
      ));
    }
    if (isArray(data.movements)) {
      this.movements = ListFactory.createRows(
        ShipmentMovement,
        data.movements.map(movement => ({
          ...movement,
          shipmentCompany: this.company,
        }))
      );
    }
    // eslint-disable-next-line vue/max-len
    if (isArray(data.attachments)) this.attachments = ListFactory.create(Attachment, data.attachments);

    if (data.createdAt) this.createdAt = new SuperDate(data.createdAt);
    if (data.acceptedAt) this.acceptedAt = new SuperDate(data.acceptedAt);
    if (data.issuedAt) this.issuedAt = new SuperDate(data.issuedAt);
    if (data.issueDay) this.issueDay = new SuperDate(data.issueDay);
    if (data.refundedAt) this.refundedAt = new SuperDate(data.refundedAt);

    if (data.timeLimit) this.timelimitAt = new SuperDate(data.timeLimit);

    if (data.preliminary) this.pi = new ShipmentPi(data.preliminary);
    if (data.customsWarehouse) this.customsWarehouse = new ShipmentWarehouse(data.customsWarehouse);
    if (isArray(data.fop)) {
      this.fopList = ListFactory.create(Fop, data.fop.filter(fop => fop.event !== 'refund'));
      this.fopRefundList = ListFactory.create(Fop, data.fop.filter(fop => fop.event === 'refund'));
    }

    if (data.price) {
      this.detalization = new PricingDetalization(data.price);
      this.soldDetalization = new PricingDetalization(data.price.sold);
      this.refundDetalization = new PricingDetalization(data.price.refund, true);
      this.totalDetalization = new PricingDetalization(data.price);
    }

    if (data.total) {
      this.price = new Money(data.total);
    }

    if (isValidObject(data.refundBid)) this.refundBid = new ShipmentRefundBid(data.refundBid);
    if (data.eawb || data.egn || data.eawbStatus) {
      this.eawb = data.eawb || data.egn || data.eawbStatus;
    }
    if (isValidObject(data.terminalOrder)) this.terminalOrder = new ShimpentTerminalOrder(data.terminalOrder);

    this.fillRoute();
  }

  get id () {
    return this.mawb;
  }

  get mawb () {
    return this.data.mAwb;
  }

  get isAeroflot () {
    return this.mawb.slice(0, 3) === '555';
  }

  get postAwb () {
    return this.cargo.additional.otherInfo;
  }

  get correspondenceAwb () {
    return this.cargo.additional.otherInfo;
  }

  get isPost () {
    return !!this.data.post;
  }

  get isCorrespondence () {
    return !!this.data.correspondence;
  }

  get showEawb () {
    return !this.isPost && !this.isCorrespondence;
  }

  get showElectronicAwb () {
    return this.showEawb && !this.eawbDisabled;
  }

  get isEawbWorkflowAvailable () {
    return this.showElectronicAwb && !!this.isIssued && !!this.isAccepted;
  }

  get eawbDisabled () {
    return !!this.data.eawbDisabled;
  }

  get departure () {
    return this.data.route.departure;
  }

  get arrival () {
    return this.data.route.arrival;
  }

  get direction () {
    return `${this.departure}-${this.arrival}`;
  }

  get cargo () {
    return this.cargo;
  }

  get consignee () {
    return this.consignee;
  }

  get company () {
    return this.data.company;
  }

  get shipper () {
    return this.shipper;
  }

  get expeditor () {
    return this.expeditor;
  }

  get cabinetShipper () {
    return this.cabinetShipper;
  }

  get bookings () {
    return this.bookings;
  }

  get firstBooking () {
    if (!this.activeBookings.length) return null;
    return this.activeBookings[0];
  }

  get firstBookingRaid () {
    if (!this.firstBooking) return null;
    return this.firstBooking.raid;
  }

  get movements () {
    return this.movements;
  }

  get trackingStatus () {
    return buildShipmentTracking(this).status;
  }

  get trackingTimeline () {
    return buildShipmentTracking(this).timeline;
  }

  get trackingProgress () {
    const tracking = buildShipmentTracking(this);
    return calculateTrackingProgress(tracking.timeline, tracking.status);
  }

  get trackingAlerts () {
    return buildShipmentTracking(this).alerts;
  }

  get trackingUpdatedAt () {
    return buildShipmentTracking(this).updatedAt;
  }

  get attachments () {
    return this.attachments;
  }

  get acceptType () {
    return this.data.accepted;
  }

  get isAccepted () {
    // return this.acceptType !== 'unaccepted';
    return this.data.accepted;
  }

  get isAcceptedByFFM () {
    // return this.acceptType === 'ffm';
    return this.isAccepted;
  }

  get isAcceptedByFSUFOH () {
    // return this.acceptType === 'fsu/foh';
    return this.isAccepted;
  }

  get isAcceptedByUser () {
    // return this.acceptType === 'user';
    return this.isAccepted;
  }

  get isIssued () {
    return this.data.issued;
  }

  get isDelivered () {
    return this.data.delivered;
  }

  get acceptedAt () {
    return this.acceptedAt;
  }

  get issuedAt () {
    return this.issuedAt;
  }

  get issueDay () {
    return this.issueDay;
  }

  get issueType () {
    return this.isIssued
      ? this.data.issuedByFwb ? 'FWB' : 'AWB'
      : '';
  }

  get deliveredAt () {
    return this.data.deliveredAt;
  }

  get eawb () {
    return this.eawb;
  }

  get isConsignee () {
    return this.consignee && this.consignee.isFilled;
  }

  get isShipper () {
    return this.shipper && this.shipper.isFilled;
  }

  get createdAt () {
    return this.createdAt;
  }

  get refundedBy () {
    return this.data.refundedBy;
  }

  get refundType () {
    return this.data.refundType;
  }

  get localizedRefundType () {
    if (!this.refundType) return '';
    return Vue.prototype.$localize.t(`shipments.refund.types.${this.refundType.toLowerCase()}`);
  }

  get isRefunded () {
    return this.data.refunded;
  }

  get hasRefundBid () {
    return !!this.refundBid;
  }

  get refundBidId () {
    return this.refundBid?.id;
  }

  get refundBidCreatedAt () {
    return this.refundBid?.createdAt;
  }

  get refundBidCreatedBy () {
    return this.refundBid?.createdBy;
  }

  get refundBidComment () {
    return this.refundBid?.comment;
  }

  get refundBidAttachments () {
    return this.refundBid?.attachments;
  }

  get refundBidReason () {
    return this.refundBid?.localizedReason;
  }

  get refundBidStatus () {
    return this.refundBid?.localizedStatus;
  }

  get isRefundBidProcessing () {
    return this.refundBid?.isProcessing;
  }

  get isRefundBidRejected () {
    return this.refundBid?.isRejected;
  }

  get isRefundBidCanceled () {
    return this.refundBid?.isCanceled;
  }

  addAwbPreviewHtml (data) {
    this.awbPreviewHtml = data;
  }

  addLetterPreviewHtml (data) {
    this.letterPreviewHtml = data;
  }

  addAttachments (data) {
    this.attachments = ListFactory.create(Attachment, data);
  }

  addAttachment (data) {
    this.attachments.push(new Attachment(data));
  }

  addHistory (res, clean) {
    const historyList = isValidArray(res?.list) ? res.list : [];
    const history = isValidObject(res)
      ? {
        list: historyList.map((item, index) => {
          return {
            ...item,
            id: `${res.pagination.page}-${index}`,
            isPost: this.isPost,
            isCorrespondence: this.isCorrespondence,
            company: this.company,
          };
        }),
        pagination: res.pagination,
      }
      : {
        list: [],
        pagination: {},
      };
    if (clean) this.history = new List(history, ShipmentHistory);
    else this.history.addItems(history);
  }

  get fullName () {
    return `${this.mawb} | ${this.direction}`;
  }

  get activeBookings () {
    return this.bookings.filter(booking => booking.isActive);
  }

  get notActiveBookings () {
    return this.bookings.filter(booking => !booking.isActive);
  }

  get hasBookings () {
    return !!(this.bookings && this.bookings.length);
  }

  get hasActiveBookings () {
    return !!(this.activeBookings && this.activeBookings.length);
  }

  get hasActiveVoidedBookings () {
    const voidedStatuses = ['UU', 'CN'];
    return this.activeBookings.some(booking => voidedStatuses.includes(booking.allocationCode));
  }

  get hasActiveUUBookings () {
    return this.activeBookings.some(booking => booking.allocationCode === 'UU');
  }

  get hasActiveCNBookings () {
    return this.activeBookings.some(booking => booking.allocationCode === 'CN');
  }

  get hasActiveNotVoidedBookings () {
    return this.hasActiveBookings && !this.hasActiveVoidedBookings;
  }

  get isEditBookingsAvailable () {
    return this.hasActiveNotVoidedBookings;
  }

  get isIssueAvailable () {
    return this.hasActiveNotVoidedBookings;
  }

  get isSplitAvailable () {
    return this.hasActiveNotVoidedBookings;
  }

  get isBlankUsed () {
    return this.data.awbBlankStatus === 1;
  }

  get isBookAvailable () {
    return !this.hasActiveBookings;
    /* return !this.isAcceptedByUser &&
           !this.isAcceptedByFFM &&
           !this.hasActiveBookings; */
  }

  get isReBookAvailable () {
    if (Vue.prototype.$userData?.isAdmin) {
      return (
        (this.isBlankUsed && this.hasActiveNotVoidedBookings) ||
        (!this.isBlankUsed && this.hasActiveVoidedBookings)
      );
    }
    return !this.isIssued &&
           (
             (this.isBlankUsed && this.hasActiveNotVoidedBookings) ||
             (!this.isBlankUsed && this.hasActiveVoidedBookings)
           );
  }

  get isCancelAvailable () {
    if (this.isIssued || !this.isBlankUsed || this.hasActiveVoidedBookings || this.isAccepted) return false;
    return this.activeBookings.every(booking => !booking.raid.isClosed || booking.allocationCode !== 'KK');
  }

  get isRefundAvailable () {
    return this.isIssued && !this.isRefunded && (!this.isRefundBidProcessing || this.isRefundBidRejected);
  }

  get isAcceptAvailable () {
    return !this.isAccepted && !this.hasActiveVoidedBookings;
  }

  get isTransferRoute () {
    return this.activeBookings.some(b => b.raid.direction !== this.direction);
  }

  get groupedBookings () {
    return this.route.map(r => ({
      bookings: this.sortBookingsByDeparture(this.activeBookings.filter(b => b.raid.direction === r.direction)),
      ...r,
    }));
  }

  get sortedActiveBookings () {
    return this.sortBookingsByDeparture(this.activeBookings);
  }

  get sortedNotActiveBookings () {
    return this.sortBookingsByDeparture(this.notActiveBookings);
  }

  get sortedBookings () {
    return [
      ...this.sortedActiveBookings,
      ...this.sortedNotActiveBookings,
    ];
  }

  get sortedActiveBookingHistory () {
    return this.sortBookingsByCreatedAt(this.activeBookings);
  }

  get sortedBookingHistory () {
    return [
      ...this.sortedActiveBookingHistory,
      ...this.sortBookingsByCreatedAt(this.notActiveBookings),
    ];
  }

  get hasSoldPriceInfo () {
    return this.soldDetalization && isValidArray(this.soldDetalization.list);
  }

  get hasRefundPriceInfo () {
    return this.refundDetalization && isValidArray(this.refundDetalization.list);
  }

  get hasTotalPriceInfo () {
    return this.totalDetalization && isValidObject(this.totalDetalization.total);
  }

  get hasPriceInfo () {
    return !!(this.detalization && this.detalization.list && this.detalization.list.length);
  }

  get correctionTimeout () {
    return this.data.correctionTimeout;
  }

  set correctionTimeout (value) {
    this.data.correctionTimeout = value;
  }

  get hasCorrectionTimeout () {
    return this.correctionTimeout !== null && this.correctionTimeout !== undefined;
  }

  get timelimit () {
    return this.data.timeLimitSeconds;
  }

  set timelimit (value) {
    this.data.timeLimitSeconds = value;
  }

  get hasTimelimit () {
    return this.timelimit !== null && this.timelimit !== undefined;
  }

  get hasPayments () {
    return isValidArray(this.fopList);
  }

  get fopTotal () {
    return this.#getFopTotal(this.fopList);
  }

  get fopRefundTotal () {
    return this.#getFopTotal(this.fopRefundList);
  }

  get fopListWithTotal () {
    return this.fopList.concat([this.fopTotal]);
  }

  get fopRefundListWithTotal () {
    return this.fopRefundList.concat([this.fopRefundTotal]);
  }

  #getFopTotal (list) {
    const totalPrice = list.reduce((price, fop) => (price + (fop.price?.value || 0)), 0);
    return new Fop({
      clientName: `${Vue.prototype.$localize.t('base.total')}:`.toUpperCase(),
      price: {
        value: totalPrice,
        currency: this.fopList[0]?.price?.currency || '',
      },
    });
  }

  get hasRefundFopList () {
    return this.fopRefundList.length > 0;
  }

  get totalPrice () {
    if (!this.detalization) return '';
    return this.detalization.initialPrice;
  }

  startTimer (seconds = 1) {
    if (!this.hasCorrectionTimeout) return;
    this.timer = setInterval(() => {
      if (this.correctionTimeout <= 0) this.clearTimer();
      this.correctionTimeout -= 1;
    }, 1000 * seconds);
  }

  clearTimer () {
    if (!this.timer) return;
    clearInterval(this.timer);
  }

  startTimelimitTimer (seconds = 1) {
    if (!this.hasTimelimit) return;
    this.timelimitTimer = setInterval(() => {
      if (this.timelimit <= 0) this.clearTimelimitTimer();
      this.timelimit -= 1;
    }, 1000 * seconds);
  }

  clearTimelimitTimer () {
    if (!this.timelimitTimer) return;
    clearInterval(this.timelimitTimer);
  }

  sortBookingsByDeparture (bookings) {
    return bookings.sort(
      (a, b) => a.raid.departure?.date?.nativeDate?.getTime() - b.raid.departure?.date?.nativeDate?.getTime()
    );
  }

  sortBookingsByCreatedAt (bookings) {
    return bookings.sort(
      (a, b) => b.createdAt?.nativeDate?.getTime() - a.createdAt?.nativeDate?.getTime()
    );
  }

  fillRoute () {
    if (!this.isTransferRoute) {
      this.route = [{
        direction: this.direction,
        departure: this.departure,
        arrival: this.arrival,
      }];

      return;
    }

    this.route = [];

    const shipmentDeparture = this.data?.route?.departure;
    const shipmentArrival = this.data?.route?.arrival;
    const routeBookings = this.sortedActiveBookings.filter(
      booking => booking?.raid?.direction &&
        booking?.raid?.departure?.airport &&
        booking?.raid?.arrival?.airport
    );

    if (!routeBookings.length) {
      return;
    }

    const graph = routeBookings.reduce(
      (acc, booking) => {
        const departureUid = booking.raid.departure.airport;
        const arrivalUid = booking.raid.arrival.airport;

        if (!acc.byDeparture.has(departureUid)) {
          acc.byDeparture.set(departureUid, []);
        }

        acc.byDeparture.get(departureUid).push(booking);
        acc.arrivals.add(arrivalUid);

        return acc;
      },
      {
        byDeparture: new Map(),
        arrivals: new Set(),
      }
    );

    const startBookings = routeBookings.filter(
      booking => !graph.arrivals.has(booking.raid.departure.airport)
    );

    const paths = [];
    const fallbackStarts = startBookings.length
      ? startBookings
      : routeBookings;

    const stack = fallbackStarts
      .slice()
      .reverse()
      .map(booking => ({
        booking,
        path: [],
        visitedDirections: new Set(),
      }));

    while (stack.length) {
      const current = stack.pop();
      const booking = current.booking;
      const direction = booking?.raid?.direction;
      const bookingKey = booking?.raid?.id || direction;

      if (!bookingKey || !direction || current.visitedDirections.has(bookingKey)) {
        continue;
      }

      const nextPath = [...current.path, booking];
      const nextVisited = new Set(current.visitedDirections);
      nextVisited.add(bookingKey);

      if (booking.raid.arrival.airport === shipmentArrival) {
        paths.push(nextPath);
        continue;
      }

      const nextBookings = graph.byDeparture.get(booking.raid.arrival.airport) || [];
      const availableNextBookings = nextBookings.filter(
        nextBooking => !nextVisited.has(nextBooking.raid.id || nextBooking.raid.direction)
      );

      if (!availableNextBookings.length) {
        paths.push(nextPath);
        continue;
      }

      for (const nextBooking of availableNextBookings.slice().reverse()) {
        stack.push({
          booking: nextBooking,
          path: nextPath,
          visitedDirections: nextVisited,
        });
      }
    }

    const matchedPath = paths.find(path => {
      const firstBooking = path[0];
      const lastBooking = path[path.length - 1];

      return firstBooking?.raid.departure.airport === shipmentDeparture &&
        lastBooking?.raid.arrival.airport === shipmentArrival;
    }) || paths[0] || [];

    this.route = matchedPath.map(booking => ({
      direction: booking.raid.direction,
      departure: booking.raid.departure.airport,
      arrival: booking.raid.arrival.airport,
    }));
  }

  get abilityProps () {
    return {
      company: this.company,
      departure: this.departure,
      arrival: this.arrival,
    };
  }
};
